/**
 * Validation of the store's durable state (plan D6): resolves the scheme +
 * overrides, settles `validateKeymap` to a fixed point (`settleKeymap`) and
 * folds every `overridesPatch` into the overrides, so the caller can commit
 * keymap, overrides and notices in ONE store update — a displaced binding is
 * persisted in the same write as the mutation that displaced it, and the
 * committed keymap is the one a reload of that write would compute.
 */
import { keybindingCommandIds } from '../model/defaultKeybindings';
import type { KeybindingCommandId, KeybindingNotice, Keymap } from '../model/keybindingTypes';
import { schemeFor } from './keybindingSchemes';
import { unknownOverrideIdsOf } from './normalizeOverrides';
import { sameNotice, settleKeymap } from './settleKeymap';
import type {
  KeybindingsData,
  KeybindingsSnapshot,
  KeybindingsStoreEnvironment,
} from './keybindingsStoreTypes';

export type DerivedKeybindings = Omit<KeybindingsData, 'undoSnapshot'> & {
  /** True when validation wrote durable losers / reserved removals into the overrides. */
  readonly patched: boolean;
  /** False when `settleKeymap` gave up at MAX_SETTLE_PASSES (a bug; dev builds warn, FR6). */
  readonly converged: boolean;
};

export type DeriveOptions = {
  /** The command the user just changed; it wins same-scope collisions (D6). */
  readonly mutatedCommandId?: KeybindingCommandId;
  /** The chords that change added to that command (see `validateKeymap`). */
  readonly mutatedChords?: readonly string[];
  /** Reused for referential stability and, when carrying, for its durable notices. */
  readonly previous?: Pick<KeybindingsData, 'keymap' | 'notices'>;
  /**
   * Re-validation without a mutation (layout revision) keeps the durable
   * notices of the last mutation or hydration: they describe a change that
   * already happened, and the patch that caused them no longer re-fires.
   */
  readonly carryDurableNotices?: boolean;
  /** Who made the durable changes of this derivation (J2): a user change, or an automatic repair (default). */
  readonly origin?: 'mutation' | 'automatic';
};

export function sameKeymap(left: Keymap, right: Keymap): boolean {
  return left === right || keybindingCommandIds.every((id) =>
    left[id].length === right[id].length && left[id].every((chord, index) => chord === right[id][index]));
}

/** Order-insensitive: carried notices may sit elsewhere than validation first put them. */
function sameNoticeSet(left: readonly KeybindingNotice[], right: readonly KeybindingNotice[]): boolean {
  return left.length === right.length && left.every((notice) => right.some((other) => sameNotice(notice, other)));
}

export function deriveKeybindings(
  snapshot: KeybindingsSnapshot,
  environment: KeybindingsStoreEnvironment,
  options: DeriveOptions = {},
): DerivedKeybindings {
  const settled = settleKeymap(snapshot.overrides, {
    scheme: schemeFor(snapshot.scheme, environment.schemes),
    platform: environment.platform,
    layoutMap: environment.layout.getSnapshot(),
    isDev: environment.isDev,
  }, {
    mutatedCommandId: options.mutatedCommandId,
    mutatedChords: options.mutatedChords,
    previousKeymap: options.mutatedCommandId ? options.previous?.keymap : undefined,
  });
  const { result, overrides } = settled;
  const origin = options.origin ?? 'automatic';
  const durable = settled.durableNotices.map((notice) => ({ ...notice, origin }));
  const fresh = [
    ...durable,
    ...result.notices.filter((notice) => !settled.durableNotices.some((kept) => sameNotice(kept, notice))),
  ];
  const previous = options.previous;
  const carried = options.carryDurableNotices && previous
    ? previous.notices.filter((notice) =>
      notice.persisted && !fresh.some((item) => sameNotice(item, notice)))
    : [];
  const notices = [...carried, ...fresh];
  // Previous objects are reused when equal, so callers (and the effective
  // keymap publication) can detect a no-op by identity.
  return {
    scheme: snapshot.scheme,
    overrides,
    keymap: previous && sameKeymap(previous.keymap, result.keymap) ? previous.keymap : result.keymap,
    notices: previous && sameNoticeSet(previous.notices, notices) ? previous.notices : notices,
    unknownOverrideIds: unknownOverrideIdsOf(overrides),
    patched: overrides !== snapshot.overrides,
    converged: settled.converged,
  };
}
